import type { Dictionary } from '../types';

const en: Dictionary = {
  meta: {
    title: 'Codex Algeria | Software development agency in Algiers',
    description:
      'Codex Algeria (codexalgerie.com) is an Algiers software studio since 2014 — custom software, mobile apps, websites, cloud and cybersecurity. Online quote. SARL Groupe Codex.',
  },
  nav: {
    about: 'About',
    services: 'Services',
    work: 'Work',
    blog: 'Blog',
    contact: 'Contact',
    startProject: 'Free quote',
    menu: 'Menu',
    close: 'Close',
    skip: 'Skip to content',
    backToTop: 'Codex — back to top',
  },
  theme: {
    light: 'Light',
    dark: 'Dark',
    toLight: 'Switch to light mode',
    toDark: 'Switch to dark mode',
  },
  lang: {
    label: 'Language',
    en: 'EN',
    fr: 'FR',
    ar: 'AR',
  },
  hero: {
    kicker: 'Your trusted technology partner',
    status: 'Est. 2014 — Independent',
    line1: 'We build',
    line2: 'digital',
    line3: 'futures',
    body: 'Codex is Algeria’s premier IT agency in Algiers — designing and developing intelligent web & mobile applications, cloud and cybersecurity for teams across Africa, Europe and the Middle East.',
    seeWork: 'See selected work',
    start: 'Start a\nproject',
    scroll: 'Scroll',
    marquee: [
      'Mobile Apps',
      'Web Development',
      'SaaS Development',
      'Cloud & DevOps',
      'Data Analytics',
      'Cybersecurity',
    ],
  },
  about: {
    index: '01',
    label: 'About',
    title1: 'The',
    title2: 'studio',
    leadBefore: 'Since 2014, Codex has been your trusted partner turning ambitious ideas into ',
    leadAccent: 'working software',
    leadAfter: ' — for enterprises, clinics, marketplaces and startups across three continents.',
    body: "We're a cross-functional team of engineers, designers and strategists based in Algeria. We partner deeply with each client — then design and build intelligent applications properly, with no black boxes. Codex has also taken part in many tech and export expos.",
    credentials: 'Credentials',
    credentialItems: [
      'ISO 9001 certified quality management',
      'GDPR & data-protection compliant delivery',
      'Award-winning product & UX design team',
      '99.9% uptime SLA on managed platforms',
    ],
    expos: 'Expos & summits',
    exposLead:
      'Active on tech and export stages — meeting partners, clients, and the wider digital ecosystem.',
    expoItems: ['Web Expo', 'Web Summit', 'Algeria Export Fair', 'AFES'],
    stats: [
      { label: 'Years in business' },
      { label: 'Projects shipped' },
      { label: 'Active clients' },
      { label: 'Continents served' },
    ],
    tech: 'Technologies we work with',
    algiers: 'Codex — Algiers',
  },
  services: {
    index: '02',
    label: 'Services',
    title1: 'What',
    title2: 'we',
    title3: 'do',
    ctaBody: 'Need a tailored IT solution for your business in Algeria or abroad? We scope every engagement individually.',
    cta: 'Discuss your project',
    processTitle: 'How we work',
    processLead:
      'A clear process with no surprises — you always know where your project stands.',
    processSteps: [
      {
        title: 'Discovery',
        detail:
          'Scoping workshop: goals, users, constraints and budget. You leave with a clear, costed plan.',
      },
      {
        title: 'Design',
        detail:
          'Clickable prototypes validated together before a single line of code is written.',
      },
      {
        title: 'Build',
        detail: 'Short sprints, weekly demos, continuously tested and audited code.',
      },
      {
        title: 'Launch & care',
        detail:
          'Production rollout, team training and ongoing maintenance with a 99.9% SLA.',
      },
    ],
    readyTitle: 'Ready-made products',
    readyLead:
      'Beyond custom builds, Codex ships proven product bases — faster to launch, branded and tuned to your business rules.',
    readyCta: 'Request a product demo / quote',
    readyItems: [
      {
        name: 'VTC',
        detail: 'Ride-hailing style app — rider, driver and dispatch, ready to adapt to your fleet.',
      },
      {
        name: 'Food delivery',
        detail: 'Orders, couriers, restaurants and tracking — meal delivery pack.',
      },
      {
        name: 'Flexy',
        detail: 'Flexy / mobile top-up style services for the Algerian market.',
      },
      {
        name: 'E-learning',
        detail: 'Courses, learners, content and progress — online training platform.',
      },
    ],
    items: [
      {
        title: 'Mobile Apps',
        detail:
          'Native-quality iOS and Android experiences from a single codebase. We design for offline-first, low-bandwidth realities — because your users are not always on fibre.',
        tags: ['React Native', 'iOS & Android', 'Offline-first', 'App store delivery'],
      },
      {
        title: 'Web Development',
        detail:
          'Fast, accessible web applications built on modern frameworks. Marketing sites that load instantly, and complex dashboards that stay responsive under real-world data.',
        tags: ['React / Next.js', 'Design systems', 'Headless CMS', 'Performance audits'],
      },
      {
        title: 'SaaS Development',
        detail:
          'Multi-tenant products and business software — from SaaS MVP to internal platforms. We own discovery, architecture, build, and long-term support.',
        tags: ['Product discovery', 'Multi-tenant architecture', 'API design', 'Legacy modernisation'],
      },
      {
        title: 'Cloud & DevOps',
        detail:
          'Migration, infrastructure-as-code and CI/CD pipelines that let your team ship daily instead of quarterly. Scalable by design, and priced for what you actually use.',
        tags: ['AWS / Azure', 'Kubernetes', 'Terraform', 'SRE & monitoring'],
      },
      {
        title: 'Data Analytics',
        detail:
          'Pipelines, warehouses and BI dashboards that turn raw operational data into decisions. From first ETL job to ML-assisted forecasting.',
        tags: ['Data pipelines', 'BI dashboards', 'Forecasting', 'ML integration'],
      },
      {
        title: 'Cybersecurity',
        detail:
          'Security audits, penetration testing and 24/7 monitoring for critical infrastructure. We find the weaknesses before someone else does.',
        tags: ['Pen testing', 'Security audits', 'SOC monitoring', 'Compliance'],
      },
    ],
  },
  work: {
    index: '03',
    label: 'Selected work',
    title1: 'Field',
    title2: 'notes',
    scrollExplore: 'Scroll to explore',
    projectsCount: 'projects',
    moreShipped: 'And 190+ more shipped since 2014',
    yoursNext: 'Yours next?',
    swipeHint: 'Swipe to browse · tap to open',
    openCase: 'Open case study',
    highlights: 'Key highlights',
    similar: 'Start a similar project',
    close: 'Close case study',
  },
  contact: {
    index: '05',
    label: 'Contact',
    reply: 'Replies within 24h',
    title1: "Let's",
    title2: 'talk',
    name: 'Name *',
    email: 'Email *',
    company: 'Company',
    project: 'Project *',
    namePh: 'Your name',
    emailPh: 'you@company.com',
    companyPh: 'Company or organisation',
    projectPh: 'What are you building? Timeline, goals, constraints…',
    send: 'Send message',
    preparing: 'Preparing…',
    ready: 'Ready to send',
    sentNote: 'Draft opened in your email app — send when ready.',
    whatsapp: 'Or chat on WhatsApp',
    errorRequired: 'Please fill in name, email and project details.',
    errorEmail: 'Enter a valid email address.',
    toolDescription:
      'Sends a project inquiry to Codex, the software studio in Hydra, Algiers. Use when the visitor wants to discuss a website, app, or custom software. Codex replies within 24 hours. Do not invent a price.',
    nameHint: 'Name of the person contacting Codex.',
    emailHint: 'Email address Codex should reply to.',
    companyHint: 'Company or organization. Optional.',
    projectHint: 'What they want built, including goals, timeline, and constraints.',
    agentSent: 'Inquiry prepared for Codex. The visitor still confirms sending from the form.',
    emailLabel: 'Email',
    phoneLabel: 'Phone',
    studioLabel: 'Studio',
    maps: 'Open in Maps',
    availability: 'Currently taking projects for Q4 2026',
    mailSubject: 'Project inquiry —',
  },
  projects: {
    index: '03',
    label: 'Selected work',
    title: 'Projects',
    subtitle:
      'Real products we built and still maintain — mobility, education, healthcare, marketplaces and factory ops.',
    viewCase: 'View case',
    backToProjects: 'Back to projects',
    related: 'More projects',
    role: 'Our role',
    challenge: 'Challenge',
    discovery: 'Discovery',
    approach: 'Approach',
    architecture: 'Architecture',
    delivery: 'Delivery',
    outcome: 'Outcome',
    howWeBuilt: 'How we built it',
    stack: 'Stack notes',
    highlights: 'Key highlights',
    ctaTitle: 'Yours next?',
    ctaBody: 'Tell us what you are building. We will reply with a clear next step.',
    ctaButton: 'Start a project',
    notFound: 'This project could not be found.',
    viewAll: 'All projects',
  },
  blog: {
    index: '04',
    label: 'Journal',
    title: 'Field notes',
    subtitle:
      'Practical writing on software, mobile, cloud and security — from our studio in Algiers.',
    read: 'Read',
    minRead: 'min read',
    backToBlog: 'Back to blog',
    allPosts: 'All posts',
    related: 'More from the journal',
    ctaTitle: 'Building something?',
    ctaBody: 'Get an indicative range in about two minutes — then a clear next step with Codex.',
    ctaButton: 'Free quote',
    notFound: 'This post could not be found.',
    home: 'Home',
  },
  legal: {
    index: '06',
    label: 'Legal',
    terms: 'Terms of use',
    privacy: 'Privacy policy',
    updated: 'Last updated',
    also: 'Also see',
    contactNote: 'Questions about these pages?',
    notFound: 'This legal page could not be found.',
  },
  footer: {
    blurb:
      'Your trusted technology partner in Algiers — web, mobile, cloud and cybersecurity since 2014. Seen at Web Expo, Web Summit, Algeria Export Fair and AFES.',
    sitemap: 'Sitemap',
    elsewhere: 'Elsewhere',
    legal: 'Legal',
    designed: 'Designed & built in Algiers',
    backTop: 'Back to top',
    partner: 'Partners',
  },
};

export default en;
